import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { loadedImagesCache } from '../utils/imageLoadCache';

const isInline = (src) => /^(blob:|data:)/.test(String(src || ''));

// Cached and local media skip the transition so revisits paint at once.
const startState = (src) => (!src || isInline(src) || loadedImagesCache.has(src) ? 'instant' : 'loading');

const isReady = (el) => {
  if (!el) return false;
  if (el.tagName === 'VIDEO') return el.readyState >= 1;
  return el.complete && el.naturalWidth > 0;
};

/**
 * Loading state for an <img> or <video>: a shimmer while it loads, a short
 * reveal when it arrives (styles: .kj-media in styles/loaders.css).
 *
 * Put `ref` and `className` on the element and call `done` from its onLoad
 * (images) or onLoadedData/onLoadedMetadata (videos), `fail` from onError.
 * `probe: true` preloads `src` itself, for a <video> poster that fires no
 * load event of its own.
 */
export default function useMediaReveal(src, { probe = false } = {}) {
  const ref = useRef(null);
  const [state, setState] = useState(() => startState(src));

  useLayoutEffect(() => {
    setState(!probe && isReady(ref.current) ? 'instant' : startState(src));
  }, [src, probe]);

  const done = useCallback(() => {
    if (src && !isInline(src)) loadedImagesCache.add(src);
    setState((s) => (s === 'loading' ? 'loaded' : s));
  }, [src]);

  const fail = useCallback(() => setState('instant'), []);

  useEffect(() => {
    if (!probe || state !== 'loading') return undefined;
    const img = new Image();
    img.onload = done;
    img.onerror = fail;
    img.src = src;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [probe, state, src, done, fail]);

  return { ref, className: `kj-media is-${state}`, done, fail };
}
